import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";
import { API } from "../../../config";
import axios, { all } from "axios";
import { CerealType, DataTableColumns } from "./DataTableTypes";

import {
  addHours,
  addHoursSameTime,
  setZeroHrs,
} from "../dashboard/dashboardAsyncActions";
import {
  initialState,
  resetDataTableFilters,
  setFiltersSelected,
} from "../../slices/dataTable/dataTableFilters";
import { setPastActionIds } from "../../slices/data/dataSampleView";

const pagesToPreFetch = (totalPages: number) => {
  let array = [] as number[];
  for (let i = 0; i < totalPages; i++) {
    array.push(i);
  }
  return array;
};

export const createCerealDictionary = async (
  cerealInformation: CerealType[],
  store: any
): Promise<Record<number, CerealType>> => {
  if (cerealInformation.length > 0) {
    return cerealInformation.reduce((acc, cereal) => {
      acc[cereal.textId.id] = cereal;
      return acc;
    }, {} as Record<number, CerealType>);
  } else {
    // First render with no cereal info, dispatch fetchCerealTypeDataTableDictionary
    const result = await store.dispatch(fetchCerealTypeDataTableDictionary());
    const cerealData = result.payload as CerealType[];
    return cerealData.reduce((acc, cereal) => {
      acc[cereal.textId.id] = cereal;
      return acc;
    }, {} as Record<number, CerealType>);
  }
};

export const fetchDataColumns = createAsyncThunk(
  "SharedDataReducer/fetchDataColumns",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const extraColumns = [
      {
        name: "id",
        hidden: false,
        hideable: false,
        localizedName: locale,
        columnType: "data",
        useShortResult: false,
        alwaysHide: false,
        foregroundColor: "#000000",
        backgroundColor: "#FFFFFF",
      },

      // Disable for now the status column
      // {
      //   name: "status",
      //   hidden: true,
      //   hideable: true,
      //   localizedName: "status",
      //   columnType: "data",
      //   useShortResult: false,
      //   alwaysHide: false,
      //   foregroundColor: "#000000",
      //   backgroundColor: "#FFFFFF",
      // },
    ];

    try {
      const result = await axios.get<DataTableColumns[]>(
        `${API}/portal/data-columns`,
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        }
      );

      const response = result.data;

      const allColumns = extraColumns.concat(response);

      const columns = allColumns.map((columns) => ({
        field: columns.name,
        headerName: columns.localizedName
          ? columns.localizedName
          : columns.name,
        disableColumnMenu: true,
        show: columns.hidden === false,
        description: "",
        sorteable: false,
        width: 200,
        operators: [],
        static: false,
        columnType: columns.columnType,
        alwaysHide: columns.alwaysHide ?? false,
        useShortResult: columns.useShortResult ?? false,
        foregroundColor: columns.foregroundColor ?? "#000000",
        backgroundColor: columns.backgroundColor ?? "#FFFFFF",
      }));

      const filterAlwaysHideColumns = columns?.filter((c) => !c.alwaysHide);

      return filterAlwaysHideColumns;
    } catch (err) {
      store.rejectWithValue(err);
    }
  }
);

export const fetchCerealTypeDataTableDictionary = createAsyncThunk(
  "SharedDataReducer/fetchCerealTypeDataTable",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const { cerealInformation } = state.DataTableReducer;

    if (cerealInformation.length > 0) {
      return cerealInformation;
    }
    try {
      const response = await axios.get(`${API}/core/cereal-type`, {
        headers: {
          "Accept-Language": locale,
          Authorization: `Bearer ${user!.token}`,
        },
      });

      return response.data.data as CerealType[];
    } catch (error: any) {
      return error;
    }
  }
);

export const dataTableActionsIds = createAsyncThunk(
  "SharedDataReducer/dataTableActionsIds",
  async (arg: { page: number; rowOptions: number }, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const {
      dataDateActivity,
      filtersSpecies,
      filtersLocation,
      filterUsers,
      filterActions,
    } = state.DataFiltersReducer;

    const manageSkip = () => {
      if (arg.page === 0) {
        return "";
      } else {
        return `$skip=${arg.page * arg.rowOptions}`;
      }
    };
    const fromDate = `createdAt[$gt]=${addHours(
      setZeroHrs(dataDateActivity[0])
    ).toISOString()}`;

    const toDate = `createdAt[$lt]=${addHours(
      setZeroHrs(dataDateActivity[1])
    ).toISOString()}`;

    const filterCrops = filtersSpecies
      ?.map((specie) => `&cropTextId[$in][]=${specie}`)
      .flat()
      .join("");

    const filterLocation = filtersLocation
      ?.map((location) => `&geoPointName[$in][]=${location}`)
      .flat()
      .join("");

    const filtersUsers = filterUsers
      ?.map((users) => `&username[$in][]=${users}`)
      .flat()
      .join("");

    const filtersActions = filterActions
      ?.map((actions) => `&scenarioNameTextId[$in][]=${actions}`)
      .flat()
      .join("");

    const filterCount = [
      filtersSpecies,
      filtersLocation,
      filterUsers,
      filterActions,
    ];

    const TotalFilters = filterCount
      .filter(function (element) {
        return element !== undefined;
      })
      .flat();

    const queryParams = [
      manageSkip(),
      `$limit=${arg.rowOptions}`,
      fromDate,
      toDate,
    ]
      .filter(Boolean)
      .join("&");

    store.dispatch(setFiltersSelected(TotalFilters.length));
    try {
      const result = await axios.get(
        `${API}/portal/managed-data-table?userFacingDeletion=false&${queryParams}${
          filterCrops ?? ""
        }${filterLocation ?? ""}${filtersUsers ?? ""}${filtersActions ?? ""}`,
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        }
      );

      const response = result;

      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const fetchDataArrays = createAsyncThunk(
  "SharedDataReducer/fetchDataArrays",
  async (arg: { arrayPages: number[]; rowOptions: number }, store) => {
    try {
      const fetchThemAll = arg.arrayPages.map((timesToFetch) =>
        store.dispatch(
          dataTableActionsIds({
            page: timesToFetch,
            rowOptions: arg.rowOptions,
          })
        )
      );
      const fetch = await Promise.all(fetchThemAll);
      const transform = fetch.map((data) => data.payload.data);
      const flatTransform = transform.flat();
      const mapPastActionId = flatTransform.map((info) => info.pastActionId);

      store.dispatch(setPastActionIds(mapPastActionId));
    } catch (error: any) {
      return store.rejectWithValue(error);
    }
  }
);

export const dataTableActions = createAsyncThunk(
  "SharedDataReducer/dataTableActions",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const {
      dataDateActivity,
      filtersSpecies,
      filtersLocation,
      filterUsers,
      actions,
    } = state.DataFiltersReducer;

    const { cerealInformation } = state.DataTableReducer;
    const { DataTableOptions } = state.SharedDataReducer;

    const filterActions = actions.valuesSelected.map(
      (a) => a.userFacingNameTextId
    );

    const manageSkip = () => {
      if (DataTableOptions.page === 0) {
        return "";
      } else {
        return `$skip=${DataTableOptions.page * DataTableOptions.rowOptions}`;
      }
    };
    const fromDate = `createdAt[$gt]=${addHours(
      setZeroHrs(dataDateActivity[0])
    ).toISOString()}`;

    const toDate = `createdAt[$lt]=${addHoursSameTime(
      setZeroHrs(dataDateActivity[1])
    ).toISOString()}`;

    const filterCrops = filtersSpecies
      ?.map((specie) => `&cropTextId[$in][]=${specie}`)
      .flat()
      .join("");

    const filterLocation = filtersLocation
      ?.map((location) => `&geoPointName[$in][]=${location}`)
      .flat()
      .join("");

    const filtersUsers = filterUsers
      ?.map((users) => `&username[$in][]=${users}`)
      .flat()
      .join("");

    const filtersActions = filterActions
      ?.map((actions) => `&scenarioNameTextId[$in][]=${actions}`)
      .flat()
      .join("");

    const filterCount = [
      filtersSpecies,
      filtersLocation,
      filterUsers,
      filterActions,
    ];

    const TotalFilters = filterCount
      .filter(function (element) {
        return element !== undefined;
      })
      .flat();

    const queryParams = [
      manageSkip(),
      `$limit=${DataTableOptions.rowOptions}`,
      fromDate,
      toDate,
    ]
      .filter(Boolean)
      .join("&");

    store.dispatch(setFiltersSelected(TotalFilters.length));

    try {
      const result = await axios.get(
        `${API}/portal/managed-data-table?userFacingDeletion=false&${queryParams}${
          filterCrops ?? ""
        }${filterLocation ?? ""}${filtersUsers ?? ""}${filtersActions ?? ""}`,
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        }
      );

      const cerealDictionary = await createCerealDictionary(
        cerealInformation,
        store
      );

      const currentPageData = result.data.data;

      const newData = currentPageData.map((data) => ({
        ...data,
        cropInfoColor: cerealDictionary[data.cropTextId.id] || null,
      }));

      // Pre-fetch next pages
      const pagesToPreFetchValues = pagesToPreFetch(DataTableOptions.page + 2);

      store.dispatch(
        fetchDataArrays({
          arrayPages: pagesToPreFetchValues,
          rowOptions: DataTableOptions.rowOptions,
        })
      );

      const response = {
        data: newData,
        total: result.data.total,
        limit: result.data.limit,
      };

      return response as { data: any[]; total: number; limit: number };
    } catch (error) {
      return error;
    }
  }
);

export const setFiltersDataTableActions = createAsyncThunk(
  "SharedDataReducer/setFiltersDataTableActions",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const {
      dataDateActivity,
      filtersSpecies,
      filtersLocation,
      filterUsers,
      actions,
    } = state.DataFiltersReducer;

    const { DataTableOptions } = state.SharedDataReducer;

    const { cerealInformation } = state.DataTableReducer;
    const filterActions = actions.valuesSelected.map(
      (a) => a.userFacingNameTextId
    );

    const fromDate = `createdAt[$gt]=${addHours(
      setZeroHrs(dataDateActivity[0])
    ).toISOString()}`;

    const toDate = `createdAt[$lt]=${addHoursSameTime(
      setZeroHrs(dataDateActivity[1])
    ).toISOString()}`;

    const filterCrops = filtersSpecies
      ?.map((specie) => `&cropTextId[$in][]=${specie}`)
      .flat()
      .join("");
    const filterLocation = filtersLocation
      ?.map((location) => `&geoPointName[$in][]=${location}`)
      .flat()
      .join("");

    const filtersUsers = filterUsers
      ?.map((users) => `&username[$in][]=${users}`)
      .flat()
      .join("");

    const filtersActions = filterActions
      ?.map((actions) => `&scenarioNameTextId[$in][]=${actions}`)
      .flat()
      .join("");

    const filterCount = [
      filtersSpecies,
      filtersLocation,
      filterUsers,
      filterActions,
    ];

    const TotalFilters = filterCount
      .filter(function (element) {
        return element !== undefined;
      })
      .flat();

    const queryParams = [
      `$skip=0`,
      `$limit=${DataTableOptions.rowOptions}`,
      fromDate,
      toDate,
    ].join("&");
    store.dispatch(setFiltersSelected(TotalFilters.length));
    try {
      const result = await axios.get(
        `${API}/portal/managed-data-table?userFacingDeletion=false&${queryParams}${
          filterCrops ?? ""
        }${filterLocation ?? ""}${filtersUsers ?? ""}${filtersActions ?? ""}`,
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        }
      );

      const cerealDictionary = await createCerealDictionary(
        cerealInformation,
        store
      );

      const newData = result.data.data.map((data) => ({
        ...data,
        cropInfoColor: cerealDictionary[data.cropTextId.id] || null,
      }));

      const pagesToPreFetchValues = pagesToPreFetch(DataTableOptions.page + 2);
      store.dispatch(
        fetchDataArrays({
          arrayPages: pagesToPreFetchValues,
          rowOptions: DataTableOptions.rowOptions,
        })
      );

      const response = {
        data: newData,
        total: result.data.total,
        limit: result.data.limit,
      };

      return response as { data: any[]; total: number; limit: number };
    } catch (error) {
      return error;
    }
  }
);

export const resetDataTableActions = createAsyncThunk(
  "SharedDataReducer/resetDataTableActions",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;

    const { cerealInformation } = state.DataTableReducer;
    const { DataTableOptions } = state.SharedDataReducer;

    store.dispatch(resetDataTableFilters());

    const fromDate = `createdAt[$gt]=${addHours(
      setZeroHrs(initialState.dataDateActivity[0])
    ).toISOString()}`;

    const toDate = `createdAt[$lt]=${addHours(
      setZeroHrs(initialState.dataDateActivity[1])
    ).toISOString()}`;

    const queryParams = [fromDate, toDate, "$limit=100"].join("&");

    store.dispatch(setFiltersSelected(0));

    try {
      const result = await axios.get(
        `${API}/portal/managed-data-table?userFacingDeletion=false&${queryParams}`,
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        }
      );

      const cerealDictionary = await createCerealDictionary(
        cerealInformation,
        store
      );

      const newData = result.data.data.map((data) => ({
        ...data,
        cropInfoColor: cerealDictionary[data.cropTextId.id] || null,
      }));

      const pagesToPreFetchValues = pagesToPreFetch(DataTableOptions.page + 2);

      store.dispatch(
        fetchDataArrays({
          arrayPages: pagesToPreFetchValues,
          rowOptions: DataTableOptions.rowOptions,
        })
      );
      const response = {
        data: newData,
        total: result.data.total,
        limit: result.data.limit,
      };

      return response as { data: any[]; total: number; limit: number };
    } catch (error) {
      return error;
    }
  }
);
